import { admitReportSources } from '../../../lib/reportGeneration/reportEvidenceSourceAdmission';
import React from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, History, ChevronUp, ChevronDown, CheckCircle2, AlertTriangle, ArrowRight, ArrowUpRight, Circle, Star } from 'lucide-react';
import { Button } from '../../ui/button';
import { SiteRagReport, DataSource } from '../../../types/siteRag';
import { ReportSourceNote } from './ReportSourceNote';
import type { SiteEntity } from '../../../types/site';
import { ReportTheme } from '../../../types/reportTheme';
import { EditorialSectionShell } from './EditorialSectionShell';
import { GENERIC_SECTION_INSUFFICIENT_MESSAGE } from '../../../lib/sectionFallbacks/derivedFallbacks';
import { ConclusionCallout } from '../ConclusionCallout';
import { CustomBriefConclusionBody } from '../sections/CustomBrief/CustomBriefConclusionBody';
import { BlindSpotsAppendix } from './BlindSpotsAppendix';
import { EditableHeading } from './EditableHeading';
import { getReportDocuments } from '../../../lib/reportDocs';
import { walkClaimsText } from '../../../lib/walkClaimsText';
import {
  parseActionPlanPhaseDescription,
  parseRecommendationForCard,
  reportHasStructuredActionPlanDetail,
} from '../../../lib/reportGeneration/actionPlanPresentation';
// DDL-FOLLOWUP-IDREF — reads through resolveSiteDocuments for fresh state
import { resolveSiteDocuments, resolveSiteDocumentFileName, withResolvedSiteDocumentFileName } from '../../../lib/site/resolveSiteDocuments';
// DM1 removed the Coverage-checklist panel (DocChecklistPanel) from the
// report-output appendix (see breadcrumb below). The panel has since been
// deleted; the Site Evidence tab's CoverageMatrix is the live coverage surface.
import { useViewMode } from '../viewMode';
import { dedupeDocsByCanonicalName, countHiddenRevisions, type DedupedDocGroup } from '../../../lib/documentDedupe';
import type { SiteDocument } from '../../../types/siteRag';
import { formatReferenceCategory, stripCachedSuffix } from '../../../lib/referenceCategoryLabels';
import { isSentimentInfluencingReference } from '../../../lib/filterSentimentReferences';
import {
  hostnameOf,
  isAdamsAccession,
  adamsUrlForAccession,
  formatIsoDate,
} from '../../../lib/referenceUrl';
import {
  documentCategoryFromPath,
  DOCUMENT_CATEGORY_ORDER,
  DOCUMENT_CATEGORY_LABEL,
  cleanDocFileName,
  type DocumentCategory,
} from '../../../lib/documentCategoryFromPath';
import {
  documentRelevanceTier,
  type DocumentRelevanceTier,
} from '../../../lib/documentRelevanceTier';
import { buildCitedInMap } from '../../../lib/referenceBackrefs';
import { computeBodyCitedDocIds } from '../../../lib/reportCitedDocIds';
import { computeReportDocStats } from '../../../lib/reportDocStats';
import { formatElevation } from '../../../types/siteEvidence';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import { resolveDocumentLineage } from '../../../lib/resolveDocumentLineage';
import { reportDocumentPreview } from '../../../lib/reportDocumentPreview';
import { readConclusionRiskItems, findingsLabel } from '../../../lib/reportFindings';
import { resolveEffectiveConclusion } from '../../../lib/resolveEffectiveConclusion';
import { roleColor, roleColorAlpha } from '../../../lib/reportRoleColors';
import { reportSectionGapClass } from '../../../lib/reportSpacingTokens';
import { getDisplayVerdict, getVerdictCapAnnotation } from '../../../lib/reportVerdict';
import { reportShellBodyFontSize, reportHeadingInk } from '../../../lib/reportSectionHeading';
import {
  EditorialCallout,
  EditorialVerdictMark,
  EditorialDefinitionList,
} from './editorial';
import type { EditorialDefinitionItem } from './editorial';
import {
  isRetiredSimulationDataSource,
  LEGACY_SIMULATION_REFERENCE_LABEL,
  LEGACY_SIMULATION_REGEN_GUIDANCE,
} from '../../../lib/reportGeneration/legacySimulationRetirement';

const MONO = 'var(--report-body-font, inherit)';

function DocumentRelevanceMark({ tier }: { tier: DocumentRelevanceTier }): React.ReactElement {
  if (tier === 'supporting') {
    return (
      <svg
        aria-hidden="true"
        focusable="false"
        data-pdf-document-tier-mark="supporting"
        className="h-[0.85em] w-[0.85em] shrink-0"
        viewBox="0 0 16 16"
        fill="none"
      >
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 2a6 6 0 0 0 0 12Z" fill="currentColor" />
      </svg>
    );
  }
  const Icon = tier === 'primary' ? Star : Circle;
  return (
    <Icon
      aria-hidden="true"
      data-pdf-document-tier-mark={tier}
      className="h-[0.85em] w-[0.85em] shrink-0"
      fill={tier === 'primary' ? 'currentColor' : 'none'}
      strokeWidth={tier === 'reference' ? 1.75 : 2}
    />
  );
}

/** Small sub-head inside the verdict callout / conclusion grids —
 *  muted ink (was text-text-secondary). Theme-aware. */
function microConclusionHeadStyle(theme: ReportTheme): React.CSSProperties {
  return { color: roleColor(theme, 'muted') };
}

/** Small-caps mono appendix sub-head — accent ink + .16em tracking
 *  (was `text-text-secondary uppercase tracking-widest`). Theme-aware.
 *  Exported for COMBINES (fold #5): the folded Extended-Site-Data appendix
 *  subsection (ExtendedDataSection `renderAsAppendixSubsection`) renders its
 *  sub-head in this exact idiom so it matches the sibling appendix blocks. */
export function appendixHeadStyle(theme: ReportTheme): React.CSSProperties {
  return {
    fontFamily: MONO,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: roleColor(theme, 'accent'),
  };
}

interface ReferencesSectionProps {
  report: SiteRagReport;
  /** Owning site — used by getReportDocuments to resolve documentRefs against
   *  site.documents (Documents_On_Site Prompt 1). Falls back to legacy
   *  report.documents when undefined. */
  site?: SiteEntity;
  /**
   * REF-LINEAGE REF-4 — when supplied, every appendix row whose `field`
   * resolves to a SED section with `linkedDocumentRefs[*].excerptPreview`
   * surfaces the verbatim quote inline so reviewers can sanity-check
   * the extraction without opening the doc viewer. Sourced via the
   * same `resolveDocumentLineage` helper that drives the [Ref N]
   * click-through (REF-3) so the row preview and the deep-link
   * describe the same passage.
   *
   * Optional for back-compat: legacy callers (print export without
   * SED, test fixtures, the editor's flat-list mode) silently skip
   * the preview block. The row matches today's output byte-for-byte
   * when omitted.
   */
  sed?: SiteEvidenceDocument;
  theme: ReportTheme;
  /**
   * Admin-only telemetry surfaces (today: the "Recorded source context"
   * appendix listing the Maps / Places / Satellite calls that backed
   * this report). Defaults to `false` — non-admin viewers AND the PDF
   * print tab (which has no Firebase session by default) hide the
   * block. The parent (SiteRagReportView) reads `companyRole === 'admin'`
   * from the store and threads it through here. AG pack, AG1.
   */
  isAdmin?: boolean;
  isEditing: boolean;
  editedReport: SiteRagReport;
  setEditedReport: (r: SiteRagReport | ((prev: SiteRagReport) => SiteRagReport)) => void;
  headingOverrides: Record<string, string>;
  setHeading: (key: string, value: string) => void;
  isVisible: (sectionId: string) => boolean;
  sectionStyle: (sectionId: string) => React.CSSProperties;
  /** Reference editing helpers */
  editableRefs: DataSource[];
  addReference: () => void;
  updateReference: (idx: number, field: string, value: string) => void;
  removeReference: (idx: number) => void;
  /** Custom section helpers */
  customSections: Array<{ id: string; title: string; content: string }>;
  addCustomSection: () => void;
  updateCustomSection: (id: string, field: 'title' | 'content', value: string) => void;
  removeCustomSection: (id: string) => void;
  moveCustomSection: (id: string, dir: -1 | 1) => void;
  /** Edit history toggle */
  showEditHistory: boolean;
  setShowEditHistory: (v: boolean) => void;
  setIsEditing: (v: boolean) => void;
  /** When true, outer wrapper uses `display: contents` so nested `[data-section]` nodes participate in report-root flex `order`. */
  sectionOrderFlatten?: boolean;
  omitSectionSidebar?: boolean;
  sectionNumberProps?: {
    conclusion?: { 'data-section-number'?: string };
    appendix?: { 'data-section-number'?: string };
  };
  /** N1.5 — VITE_EDITORIAL_LAYOUTS on: render the editorial hero-numeral opener. */
  editorialActive?: boolean;
  /** N1.5 — 1-based TOC display numbers for the conclusion + appendix hero numerals. */
  editorialSectionNumbers?: { conclusion?: number; appendix?: number };
  /**
   * COMBINES (fold #5) — the Extended Site Data appendix subsection
   * (ExtendedDataSection with `renderAsAppendixSubsection`), rendered under
   * the Appendix & References heading BEFORE the Data Sources block. The
   * caller passes it ONLY when it will actually render (non-public-sentiment
   * fold type + not explicitly hidden + `extendedDataHasContent`), so the
   * bare-heading guard below can count a non-null slot as a renderable child.
   * Undefined ⇒ byte-identical appendix (public-sentiment, strip types,
   * hidden, or no extended data).
   */
  extendedDataSlot?: React.ReactNode;
  /**
   * R3-C1 — the Simulation & Resilience appendix subsection
   * (SimulationResilienceSection), rendered under the Appendix & References
   * heading after the Extended Site Data slot. The caller passes it ONLY when
   * the report carries ≥1 sim-evidence exhibit (`hasSimEvidenceExhibits`), so
   * `undefined` ⇒ byte-identical appendix (dormant-safe — the section is absent
   * with no evidence).
   */
  simulationResilienceSlot?: React.ReactNode;
  /** Immutable V2 artifacts materialize the canonical blind-spot section.
   * Legacy surfaces retain their committed presentation baseline. */
  immutableArtifactMode?: boolean;
}

const ReferencesSection = React.memo<ReferencesSectionProps>(({
  report, site, sed, theme, isAdmin = false, isEditing, editedReport, setEditedReport,
  headingOverrides, setHeading, isVisible, sectionStyle,
  editableRefs, addReference, updateReference, removeReference,
  customSections, addCustomSection, updateCustomSection, removeCustomSection, moveCustomSection,
  showEditHistory, setShowEditHistory, setIsEditing,
  sectionOrderFlatten = false,
  omitSectionSidebar = false,
  sectionNumberProps,
  editorialActive = false,
  editorialSectionNumbers,
  extendedDataSlot,
  simulationResilienceSlot,
  immutableArtifactMode = false,
}) => {
  // Customer-facing PDFs / DOCX exports must not include the
  // per-correction edit-history appendix — it leaks who tweaked what
  // and when, and bloats the appendix the customer actually wants
  // (data sources). Editors / readers in-product still see it.
  const audienceMode = useViewMode();
  const showEditHistoryAppendix = audienceMode !== 'export';

  // Revision-archive dedupe (Documents appendix). Defaults to ON so a
  // 7-revision z. Archive cluster collapses to one row + a "+6 earlier
  // revisions" expander. The "Show all" admin chip toggles this off
  // for users who need full traceability (legal review, audit trail).
  // Render-time projection only — site.documents is never mutated.
  const [dedupeRevisions, setDedupeRevisions] = React.useState(true);
  // Per-group expansion state — keyed by the leader doc's id.
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(() => new Set());
  const toggleGroup = React.useCallback((leaderId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(leaderId)) next.delete(leaderId);
      else next.add(leaderId);
      return next;
    });
  }, []);
  const liveDocs = React.useMemo(
    () => site?.documents ?? report.documents ?? [],
    [site?.documents, report.documents],
  );
  // RCO-2 — ground-truth citation set for the Documents-informing-this-
  // report panel. Path A walks the narrative for `[Ref N]` and routes
  // through SED lineage; Path B reads `ReportClaim.sources` doc:<id>;
  // Path C resolves `(per "filename")` against live docs (R-74).
  const citedDocIds = React.useMemo(
    () => computeBodyCitedDocIds(report, sed, { liveDocs }),
    [report, sed, liveDocs],
  );
  const effectiveConclusion = React.useMemo(
    () => resolveEffectiveConclusion(report),
    [report],
  );
  const conclusionReport = React.useMemo(
    () => (effectiveConclusion ? { ...report, conclusion: effectiveConclusion } : report),
    [report, effectiveConclusion],
  );
  // HR-QA fix (2026-06-10) — risk items hoisted out of the render IIFE so the
  // empty-conclusion guard below and the structured-verdict box share ONE read.
  const conclusionRiskItems = React.useMemo(
    () => readConclusionRiskItems(conclusionReport, { truncateAt: 'none' }),
    [conclusionReport],
  );
  // HR-QA fix — a conclusion object that exists but carries NOTHING renderable
  // (no BTM synthesis, empty strengths/risks/conditions/actions, no score
  // drivers) used to render the ConclusionCallout's "Review the structured
  // strengths, risks, conditions, and next actions below…" pointer followed by
  // a completely empty accent box (custom-brief PDFs). Treat it like a missing
  // conclusion: show the standard insufficient-data placeholder instead.
  const conclusionHasRenderableContent = React.useMemo(() => {
    if (!effectiveConclusion) return false;
    const btmSummary = effectiveConclusion.btmDecisionSummary?.trim()
      || report.btmDecisionSummary?.trim();
    return Boolean(
      btmSummary
      || (effectiveConclusion.topStrengths?.length ?? 0) > 0
      || conclusionRiskItems.items.length > 0
      || (effectiveConclusion.keyConditions?.length ?? 0) > 0
      || (effectiveConclusion.nextActions?.length ?? 0) > 0
      || (report.scoreDrivers?.length ?? 0) > 0,
    );
  }, [effectiveConclusion, conclusionRiskItems, report.btmDecisionSummary, report.scoreDrivers]);
  // Per-section expansion state for the Provided-but-not-cited disclosure.
  // Closed by default — the headline number reflects what the narrative
  // leaned on; the rest is a click-to-reveal so reviewers can still
  // audit the full prompt context if they want to.
  const [providedUncitedOpen, setProvidedUncitedOpen] = React.useState(false);
  // Theme-aware section divider (was `border-b border-slate-100`).
  // K2: vertical rhythm reads the shared spacing token (default `py-6`,
  // `py-5` when the theme density is compact) instead of a hardcoded `py-8`.
  const sectionChrome = reportSectionGapClass(theme.density);
  const sectionRule = `1px solid ${roleColor(theme, 'hair')}`;

  // Bare-heading guard — does the Appendix have ANY renderable child block?
  // Each clause mirrors the corresponding block's own null-guard below so the
  // shell (and its lone "Appendix" h2) is skipped only when every block is
  // empty. Edit mode always shows the shell so the user can add references.
  const appendixHasContent = React.useMemo<boolean>(() => {
    if (isEditing) return true;
    // 0. COMBINES (fold #5) — the Extended Site Data appendix subsection. The
    //    caller only passes the slot when it will render (content-gated via
    //    extendedDataHasContent + the archived sectionVisibility hide), so a
    //    non-null slot IS a renderable child block.
    if (extendedDataSlot != null) return true;
    if ((report.siteDiligenceEvidence?.blocks.length ?? 0) > 0) return true;
    // 1. Data Sources & References — same filter the render IIFE applies.
    const isPsr = report.reportType === 'public-sentiment';
    const hasReferences = (report.references ?? []).some((ref) => {
      const source = ref.source.toLowerCase();
      if (source.includes('unavailable') || source.includes('failed') || source.includes('default estimate')) {
        return false;
      }
      if (isPsr && !isSentimentInfluencingReference(ref)) return false;
      return true;
    });
    if (hasReferences) return true;
    // 2. Data quality notes.
    if ((report.crossValidationFindings ?? []).length > 0) return true;
    // 3. Documents — audit refs OR a populated doc set.
    if ((report.documentRefs ?? []).length > 0) return true;
    if (getReportDocuments(report, site).length > 0) return true;
    // 4. Google API placeholder (non-admin shared view) — admin block (5).
    if (!isAdmin && audienceMode === 'public-share') return true;
    if (isAdmin) return true;
    // 6. Edit History (in-product admin/editor surface, never in export).
    if (showEditHistoryAppendix && (report.editHistory?.length ?? 0) > 0) return true;
    return false;
  }, [isEditing, report, site, isAdmin, audienceMode, showEditHistoryAppendix, extendedDataSlot]);

  return (
    <div
      style={{
        fontFamily: theme.bodyFont,
        color: roleColor(theme, 'body'),
        ...(sectionOrderFlatten ? { display: 'contents' as const } : {}),
      }}
    >
      {/* Go/No-Go Conclusion.
           TSP-1 — apply `sectionStyle('conclusion')` so the section
           order from the Studio's drag-reorder is honoured (it was
           previously a no-op because the wrapper had no style). */}
      {isVisible('conclusion') && (
        <EditorialSectionShell
          id="conclusion"
          data-section="conclusion"
          data-section-id="conclusion"
          extraClassName={sectionChrome}
          style={{ ...sectionStyle('conclusion'), scrollMarginTop: '80px', borderBottom: sectionRule }}
          omitSectionSidebar={omitSectionSidebar}
          // SEC-INDENT — match the rest of the report body: self-pad only
          // when flattening (on-screen). In paged/export mode the padded
          // page wrapper owns the gutter, so self-padding here pushed the
          // conclusion 60px further RIGHT than its top-level neighbours.
          shellPadding={sectionOrderFlatten}
          theme={theme}
          {...sectionNumberProps?.conclusion}
          editorialActive={editorialActive}
          sectionNumber={editorialSectionNumbers?.conclusion}
          heading={
            <EditableHeading headingKey="conclusion" defaultText="Conclusion & Verdict" className="text-xl font-bold" isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />
          }
        >
          {/* Classic mode (flag OFF) — caller-owned head (byte-identical to the
               retired ClassicSectionShell path). Editorial mode: the shell owns
               the opener, so this is suppressed. */}
          {!editorialActive && (
            <h2 className="text-xl font-bold border-b-2 pb-2 mb-6" style={{ borderColor: reportHeadingInk(theme), color: reportHeadingInk(theme), fontFamily: theme.headingFont }} data-heading="true">
              <EditableHeading headingKey="conclusion" defaultText="Conclusion & Verdict" className="text-xl font-bold" isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />
            </h2>
          )}
          {(!effectiveConclusion || !conclusionHasRenderableContent) && !(report.reportType === 'custom-brief' && report.customBriefConclusion) ? (
            <div className="text-xs italic py-2" style={{ color: roleColor(theme, 'muted'), background: 'transparent' }} data-testid="conclusion-empty">
              {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
            </div>
          ) : report.reportType === 'custom-brief' && report.customBriefConclusion ? (
            <CustomBriefConclusionBody report={report} conclusion={report.customBriefConclusion} />
          ) : (
            <>
              {/* CONCLUSION 20B (BATCH R2) — editorial verdict band composed BENEATH
              the head, ABOVE the existing recommendation callout (the
              Exec-Summary D1 / Feasibility 18A proof pattern). EditorialVerdictMark
              renders the type-specific tier label from getDisplayVerdict(report);
              the one-line RATIONALE caption surfaces the verdict CAPS
              (firmCapacityCap / evidenceTierCap / constraintCap) via the canonical
              getVerdictCapAnnotation — the same SSOT the ConclusionCallout reads —
              so a capped verdict explains itself. The caption is OMITTED when no
              cap fired (no fabricated rationale). All fields exist (GAP: none). */}
              {(() => {
                const verdict = getDisplayVerdict(report);
                const showVerdict = (verdict.tier !== 'none' || verdict.savedDecisionPending) && verdict.label && verdict.label !== '—';
                if (!showVerdict) return null;
                const rationale = getVerdictCapAnnotation(report, verdict);
                return (
                  <div className="space-y-2" data-testid="conclusion-verdict-band">
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }} data-testid="conclusion-verdict">
                      <span
                        style={{
                          fontFamily: MONO,
                          fontSize: 10,
                          letterSpacing: '0.2em',
                          textTransform: 'uppercase',
                          color: roleColor(theme, 'muted'),
                        }}
                      >
                        Verdict
                      </span>
                      <EditorialVerdictMark theme={theme} verdict={verdict} />
                    </div>
                    {rationale && (
                      <p
                        className="text-[11px] leading-snug"
                        style={{ fontFamily: MONO, color: roleColor(theme, 'muted') }}
                        data-testid="conclusion-verdict-rationale"
                      >
                        {rationale}
                      </p>
                    )}
                  </div>
                );
              })()}
              {/* De-dup — the verdict band ABOVE already renders the evidence-tier
                  cap sentence as its verdict rationale (same getVerdictCapAnnotation
                  SSOT). Suppress the callout's own cap annotation when the band
                  shows it, so the Conclusion & Verdict section never repeats the
                  cap sentence verbatim. The band's own render condition is mirrored
                  here (cap is only non-empty once the tier is capped, which always
                  satisfies showVerdict). */}
              <ConclusionCallout
                report={conclusionReport}
                suppressCapAnnotation={(() => {
                  const v = getDisplayVerdict(report);
                  const showVerdict = (v.tier !== 'none' || v.savedDecisionPending) && !!v.label && v.label !== '—';
                  return Boolean(showVerdict && getVerdictCapAnnotation(report, v));
                })()}
              />
              {(() => {
                const { items: riskItems, source: riskSource } = conclusionRiskItems;
                const riskHeading = riskSource === 'none'
                  ? 'Key risks'
                  : findingsLabel(riskSource, report.reportType);
                const strengths = effectiveConclusion?.topStrengths ?? [];
                const conditions = effectiveConclusion?.keyConditions ?? [];
                const nextActions = effectiveConclusion?.nextActions ?? [];
                // HR-QA fix — suppress the accent box entirely when the structured
                // verdict payload is absent (no strengths, risks, conditions, or
                // next actions). Previously an empty accent-edged box rendered
                // below the callout's pointer text.
                const hasStructuredVerdictContent =
                  strengths.length > 0
                  || riskItems.length > 0
                  || conditions.length > 0
                  || nextActions.length > 0;
                if (!hasStructuredVerdictContent) return null;
                // CONCLUSION 20B (BATCH R2) — strengths / risks now compose the
                // editorial EditorialDefinitionList (was raw roleColor divs): each
                // column is ONE definition item whose TERM is the heading
                // ("Top strengths" / the risk-source heading, e.g. "Top constraints")
                // and whose VALUE is the marker-prefixed list. The list keeps the
                // CheckCircle2 / AlertTriangle status glyphs (verdict-tone signal) but
                // routes ALL typography through the editorial body font + tokens, so
                // it reads identically across journal / institutional / monograph and
                // in export mode. The card LEFT-rule + accent wash track the theme
                // accent (unchanged intent from v1.2.2). */}
                const strengthsValue = (
                  <div className="space-y-1">
                    {strengths.map((s, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3 shrink-0" style={{ color: roleColor(theme, 'positive') }} />
                        <span style={{ color: roleColor(theme, 'body') }}>{walkClaimsText(s, report.references, report.claims)}</span>
                      </div>
                    ))}
                  </div>
                );
                const risksValue = (
                  <div className="space-y-1">
                    {riskItems.map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3 shrink-0" style={{ color: roleColor(theme, 'warning') }} />
                        <span style={{ color: roleColor(theme, 'body') }}>{walkClaimsText(r, report.references, report.claims)}</span>
                      </div>
                    ))}
                  </div>
                );
                const strengthsRiskItems: EditorialDefinitionItem[] = [
                  ...(strengths.length > 0 ? [{ label: 'Top strengths', value: strengthsValue }] : []),
                  ...(riskItems.length > 0 ? [{ label: riskHeading, value: risksValue }] : []),
                ];
                return (
                  <div
                    className="py-3"
                    data-paged-split-suffix="true"
                    // This is a sequence of bounded semantic groups, not one
                    // coarse page atom. The shared paged row-group contract is
                    // deck-only, so the normal screen composition is unchanged.
                    data-layout-group="row-group"
                    data-paged-conclusion-flow="structured-findings"
                    style={{
                      // William QA 2026-06-28 — left accent rule removed; the
                      // verdict / strengths / risks block reads flush-left on cream.
                      background: 'transparent',
                    }}
                  >
                    {strengthsRiskItems.length > 0 && (
                      <div
                        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                        data-layout-group="row-group"
                        data-paged-conclusion-flow="strengths-risks"
                      >
                        {strengthsRiskItems.map((item, i) => (
                          // William QA 2026-06-29 — drop the hairline rule that sat
                          // directly above "TOP STRENGTHS"/"TOP RISKS" + tighten spacing.
                          <div
                            key={i}
                            data-layout-group="row"
                            data-paged-conclusion-group={i === 0 ? 'top-strengths' : 'key-risks'}
                          >
                            <EditorialDefinitionList theme={theme} items={[item]} hideItemRule />
                          </div>
                        ))}
                      </div>
                    )}

                    {conditions.length > 0 && (
                      // PAGEDJS-FRAG FIX — `breakInside: 'avoid'` keeps this short
                      // block from fragmenting at a page boundary. When it landed at
                      // a page break the continuation fragment lost its utility-class
                      // box spacing (heading/bullets overlapped — the user's
                      // "overlapping recommendation list"). Inter-element spacing +
                      // line-height are now INLINE (was Tailwind mt-4/pt-4/mb-2/mb-1/
                      // text-sm) so they survive the PagedJS node clone intact.
                      <div
                        data-layout-group="row"
                        data-paged-conclusion-group="conditions"
                        style={{ breakInside: 'avoid', marginTop: 16, paddingTop: 16, borderTop: `1px solid ${roleColor(theme, 'hair')}` }}
                      >
                        {/* BUG #11 — small-caps label heading (12px); body lifts to
                            the unified report body size (14px) to match the
                            standfirst + Top strengths / Top risks definition list. */}
                        <h3 className="text-xs font-semibold" style={{ ...microConclusionHeadStyle(theme), marginBottom: 8 }}>Conditions for proceeding</h3>
                        {conditions.map((c, i) => (
                          <div key={i} style={{ color: roleColor(theme, 'body'), fontSize: reportShellBodyFontSize(theme), lineHeight: 1.5, marginBottom: 4 }}>• {walkClaimsText(c, report.references, report.claims)}</div>
                        ))}
                      </div>
                    )}

                    {nextActions.length > 0 && (
                      // PAGEDJS-FRAG FIX — see Conditions above. `min-w-0` on the text
                      // span lets long action sentences wrap inside the flex row
                      // instead of overflowing past the right page margin.
                      <div
                        data-layout-group="row"
                        data-paged-conclusion-group="next-actions"
                        style={{ breakInside: 'avoid', marginTop: 16, paddingTop: 16, borderTop: `1px solid ${roleColor(theme, 'hair')}` }}
                      >
                        {/* BUG #11 — see Conditions above. */}
                        <h3 className="text-xs font-semibold" style={{ ...microConclusionHeadStyle(theme), marginBottom: 8 }}>Recommended next actions</h3>
                        {nextActions.map((a, i) => (
                          <div key={i} className="flex items-start" style={{ gap: 8, marginBottom: 4 }}>
                            <ArrowRight className="w-3 h-3 shrink-0" style={{ color: roleColor(theme, 'accent'), marginTop: 4 }} />
                            <span className="min-w-0" style={{ color: roleColor(theme, 'body'), fontSize: reportShellBodyFontSize(theme), lineHeight: 1.5 }}>{walkClaimsText(a, report.references, report.claims)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Sensitivity Analysis */}
              {report.scoreDrivers && report.scoreDrivers.length > 0 && (
                <div className="mt-6 space-y-3">
                  <h3 className="font-bold uppercase tracking-widest text-[9px]" style={appendixHeadStyle(theme)}>Score Sensitivity: What Would Change the Verdict?</h3>
                  {/* Waterfall lifted to ReportSubSectionBlocks for template reorder/hide. */}
                  <div className="space-y-2">
                    {(report.scoreDrivers || []).map((d, i) => {
                      const swing = d.ifImproved - d.ifWorsened;
                      return (
                        <div key={i} className="flex items-center gap-3 text-xs">
                          <span className="w-36 text-[10px] font-bold uppercase tracking-widest shrink-0" style={{ fontFamily: MONO, letterSpacing: '0.14em', color: roleColor(theme, 'muted') }}>{d.category}</span>
                          <div className="flex-1 h-3 relative overflow-hidden rounded-sm" style={{ background: 'transparent', border: `1px solid ${roleColor(theme, 'hair')}` }}>
                            {/* Red zone (if worsened) */}
                            <div className="absolute top-0 h-full rounded-sm" style={{ background: roleColorAlpha(theme, 'negative', 0.2), left: `${(d.ifWorsened / 100) * 100}%`, width: `${((d.currentScore - d.ifWorsened) / 100) * 100}%` }} />
                            {/* Current position marker */}
                            <div className="absolute top-0 h-full w-0.5" style={{ background: roleColor(theme, 'ink'), left: `${d.currentScore}%` }} />
                            {/* Green zone (if improved) */}
                            <div className="absolute top-0 h-full rounded-sm" style={{ background: roleColorAlpha(theme, 'positive', 0.28), left: `${d.currentScore}%`, width: `${((d.ifImproved - d.currentScore) / 100) * 100}%` }} />
                          </div>
                          {/* MONO-LEAK FIX (BATCH R2) — was the Tailwind `font-mono`
                          utility, which resolves to the APP mono token (IBM Plex
                          Mono via --font-sleek-mono). Reports must render in the
                          editorial body font, never the app mono face. Route the
                          numeric swing readout through the editorial
                          `var(--report-body-font)` (MONO const) + tabular-nums so
                          the figures still align. */}
                          <span className="text-[8px] w-24 shrink-0 text-right" style={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums', color: roleColor(theme, 'body') }}>
                            {d.ifWorsened}←{d.currentScore}→{d.ifImproved} (±{swing})
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[8px] italic" style={{ color: roleColor(theme, 'muted') }}>Shows overall score range if each category improved to 85 (green) or worsened to 25 (red). Wider bars = higher sensitivity.</p>
                </div>
              )}
            </>
          )}
        </EditorialSectionShell>
      )}

      {/* Custom Sections -- user-added content */}
      {(customSections.length > 0 || isEditing) && (
        <div className="space-y-8 pt-12" style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}>
          {customSections.map((sec, idx) => (
            <div
              key={sec.id}
              className="space-y-3 relative"
              // Keyboard reorder: when the row container has focus,
              // ArrowUp/ArrowDown swap with the neighbor — same
              // behavior as clicking the up/down buttons. tabIndex={0}
              // makes the row focusable; the buttons inside still
              // work the same with mouse/keyboard.
              tabIndex={isEditing ? 0 : -1}
              role={isEditing ? 'group' : undefined}
              aria-label={isEditing ? `Custom section: ${sec.title}` : undefined}
              onKeyDown={isEditing ? (e) => {
                if (e.key === 'ArrowUp' && idx > 0) {
                  e.preventDefault();
                  moveCustomSection(sec.id, -1);
                } else if (e.key === 'ArrowDown' && idx < customSections.length - 1) {
                  e.preventDefault();
                  moveCustomSection(sec.id, 1);
                }
              } : undefined}
            >
              {isEditing && (
                <div className="absolute -left-8 top-0 flex flex-col gap-1">
                  <button
                    onClick={() => moveCustomSection(sec.id, -1)}
                    disabled={idx === 0}
                    aria-label={`Move "${sec.title || 'section'}" up`}
                    className="text-text-secondary hover:text-text-primary disabled:opacity-20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                  ><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button
                    onClick={() => moveCustomSection(sec.id, 1)}
                    disabled={idx === customSections.length - 1}
                    aria-label={`Move "${sec.title || 'section'}" down`}
                    className="text-text-secondary hover:text-text-primary disabled:opacity-20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                  ><ArrowDown className="w-3.5 h-3.5" /></button>
                </div>
              )}
              {isEditing ? (
                <>
                  <div className="flex items-center gap-2">
                    <input className="flex-1 text-xl font-bold bg-transparent border-b-2 border-primary/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                      value={sec.title} onChange={(e) => updateCustomSection(sec.id, 'title', e.target.value)} />
                    <button onClick={() => removeCustomSection(sec.id)} className="text-danger hover:text-danger"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <textarea className="w-full h-32 p-3 text-xs text-text-secondary leading-relaxed border border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary resize-y"
                    value={sec.content} onChange={(e) => updateCustomSection(sec.id, 'content', e.target.value)}
                    placeholder="Enter section content..." />
                </>
              ) : (
                <>
                  <h2 className="text-xl font-bold border-b-2 pb-2" style={{ borderColor: reportHeadingInk(theme), color: reportHeadingInk(theme), fontFamily: theme.headingFont }} data-heading="true">{sec.title}</h2>
                  <p className="text-xs text-text-secondary leading-relaxed whitespace-pre-wrap">{sec.content}</p>
                </>
              )}
            </div>
          ))}
          {isEditing && (
            <Button variant="outline" size="sm" className="h-8 rounded-none border-dashed border-primary/30 text-primary/60 hover:bg-primary/5 uppercase tracking-widest text-[9px]"
              onClick={addCustomSection}>
              <Plus className="w-3 h-3 mr-2" /> Add Section
            </Button>
          )}
        </div>
      )}

      {/* Document checklist + "Documents informing this report" used
           to render here, between the report body and the appendix.
           Both panels were audit-trail / coverage detail — useful for
           a careful reader, noisy in the body. They've moved into the
           appendix's "Documents" subsection alongside Data Sources. */}

      {/* Appendix — references and data provenance.
           NEVER-HIDE (William's directive): the Appendix must ALWAYS render
           when the section is visible. It carries the references the body's
           inline [Ref N] tokens resolve against plus data-quality notes and
           uploaded docs, so self-hiding it stranded the reader with citations
           that pointed at nothing. The previous `appendixHasContent` null-gate
           is REMOVED; `appendixHasContent` is now repurposed to drive a single
           muted fallback line (below) so a transiently-empty appendix shows a
           "being compiled" note instead of a bare heading. Each child block
           still self-guards to null independently, so this can never strand a
           half-rendered block. `isVisible('appendix')` remains the Studio
           show/hide toggle and is intentionally still honoured. */}
      {isVisible('appendix') && (
        <EditorialSectionShell
          id="appendix"
          data-section="appendix"
          data-section-group="appendix"
          extraClassName={`${sectionChrome} space-y-8`}
          style={{ ...sectionStyle('appendix'), borderBottom: sectionRule }}
          omitSectionSidebar={omitSectionSidebar}
          // SEC-INDENT — see conclusion shell: defer the gutter to the page
          // wrapper in paged/export mode so the appendix left-aligns with
          // its top-level neighbours.
          shellPadding={sectionOrderFlatten}
          theme={theme}
          {...sectionNumberProps?.appendix}
          editorialActive={editorialActive}
          sectionNumber={editorialSectionNumbers?.appendix}
          heading={
            <EditableHeading headingKey="appendix" defaultText="Appendix & References" className="text-xl font-bold" isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />
          }
        >
          {/* Classic mode (flag OFF) — caller-owned head (byte-identical to the
               retired ClassicSectionShell path). Editorial mode: the shell owns
               the opener, so this is suppressed. */}
          {!editorialActive && (
            <h2 className="text-xl font-bold border-b-2 pb-2" style={{ borderColor: reportHeadingInk(theme), color: reportHeadingInk(theme), fontFamily: theme.headingFont }} data-heading="true"><EditableHeading headingKey="appendix" defaultText="Appendix & References" className="text-xl font-bold" isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} /></h2>
          )}

          {/* COMBINES (fold #5) — Extended Site Data, folded in as the FIRST
             appendix subsection (before Data Sources & References). The slot
             is an ExtendedDataSection in `renderAsAppendixSubsection` mode: a
             small-caps sub-head + the datasheet body, carrying its own NESTED
             `data-section="extendedData"` marker (same reconcile-safe shape as
             `dataSources` below). Passed only when it will render — see the
             prop doc + the bare-heading guard above. */}
          {extendedDataSlot}

          {/* R3-C1 — Simulation & Resilience appendix subsection (reliability
             N+1 / ride-through / B7-gated studies), a nested
             `data-section="simulationResilience"` box, same reconcile-safe shape
             as Extended Site Data. Passed only when the report carries sim
             exhibits, so it is absent (dormant-safe) otherwise. */}
          {simulationResilienceSlot}

          {report.siteDiligenceEvidence?.blocks.length ? (
            <div data-section="siteDiligenceEvidence" className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-widest" style={{ color: roleColor(theme, 'ink') }}>
                Site-Diligence Evidence
              </h3>
              {report.siteDiligenceEvidence.blocks.map(block => (
                <div key={block.id} className="space-y-2 border-l-2 pl-4" style={{ borderColor: roleColor(theme, 'hair') }}>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <h4 className="text-sm font-semibold" style={{ color: roleColor(theme, 'ink') }}>{block.label}</h4>
                    {block.qualification !== 'verified' && (block.qualification as string) !== 'inferred' && (
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: roleColor(theme, 'accent') }}>
                        {block.qualification}
                      </span>
                    )}
                  </div>
                  {block.facts.length > 0 && (
                    <ul className="list-disc pl-5 space-y-1 text-xs" style={{ color: roleColor(theme, 'body') }}>
                      {block.facts.map((fact, index) => <li key={`fact-${index}`}>{fact}</li>)}
                    </ul>
                  )}
                  {block.caveats.map((caveat, index) => (
                    <p key={`caveat-${index}`} className="text-xs italic" style={{ color: roleColor(theme, 'body') }}>
                      Caveat: {caveat}
                    </p>
                  ))}
                  {block.sources.map((source, index) => (
                    <ReportSourceNote key={`source-${index}`}>
                      Source: {source.source} · retrieved {formatIsoDate(source.retrievedAt)}
                      {source.dataAsOf ? ` · vintage ${source.dataAsOf}` : ''}
                    </ReportSourceNote>
                  ))}
                </div>
              ))}
            </div>
          ) : null}

          {/* Data Sources & References -- editable in edit mode.
             Render contract (Content-Source Fixes Prompt 4):
             - Indices are 1-based and stable — `[Ref N]` in the body
               always points at the row whose orig-index N was set at
               generation time. We do NOT dedup at render time (that
               used to shift indices and produce dead-link tokens).
             - Cited rows (`citedInThisReport === true`, or undefined for
               legacy reports) render under the main heading.
             - Uncited rows (`citedInThisReport === false`) are omitted from
               the appendix — indices stay stable for inline `[Ref N]` tokens.
             - Edit mode keeps the old flat list — the editor needs
               every row addressable. Undefined `citedInThisReport`
               is treated as cited for back-compat with legacy SEDs. */}
          {(() => {
            const isPsr = report.reportType === 'public-sentiment';
            const refsWithIdx: Array<{ ref: DataSource; origIdx: number }> = isEditing
              ? editableRefs.map((ref, origIdx) => ({ ref, origIdx }))
              : admitReportSources(report)
                .filter(row => row.admitted && (!isPsr || isSentimentInfluencingReference(row.reference)))
                .map(({ reference: ref, index }) => ({ ref, origIdx: index - 1 }));
            const allRefs = refsWithIdx.map(({ ref }) => ref);
            if (allRefs.length === 0 && !isEditing) return null;
            // POPULATE re-wire (William's directive). The non-edit list normally
            // drops rows flagged `citedInThisReport === false`. But the gen-time
            // citation marking can mis-flag EVERY row as uncited (markCitations
            // runs against pre-renumber indices/tokens, and a best-effort review
            // pass that produced no `citedRefNumbers` leaves the literal-token
            // scan to miss compound / renumbered tokens — see citationFinalize.ts
            // + renumberReferences.ts). When that happens the body still shows
            // real [Ref N] tokens but the appendix collapsed to nothing. So:
            // if the cited-only filter would yield ZERO rows yet we DO have
            // non-sentinel refs, surface ALL of them instead of an empty list —
            // mirroring the documented "undefined === cited" back-compat. This
            // shows exactly what `report.references` carries (no fabrication);
            // it just refuses to hide a citation target the body points at.
            const citedOnly = refsWithIdx.filter(({ ref }) => ref.citedInThisReport !== false);
            const citedRefs = isEditing
              ? refsWithIdx
              : (citedOnly.length > 0 ? citedOnly : refsWithIdx);

            // AP6 — back-reference map.
            // index every `[Ref N]` token by the containing section's
            // default label. Each row's render reads `citedIn.get(N)`
            // and renders the "Cited in X, Y" strip. Suppressed in
            // edit mode — would clutter the editor and the field set
            // mid-edit isn't yet citation-stable anyway.
            const citedIn = isEditing ? new Map<number, string[]>() : buildCitedInMap(report);

            const renderRow = ({ ref, origIdx }: { ref: DataSource; origIdx: number }) => (
              <div
                key={origIdx}
                id={`ref-${origIdx + 1}`}
                data-reference-row="true"
                className="flex gap-3 text-xs text-text-secondary transition-shadow rounded-sm"
                style={{ scrollMarginTop: '80px' }}
              >
                <span className="shrink-0" style={{ fontFamily: MONO, color: roleColor(theme, 'accent') }}>[{origIdx + 1}]</span>
                {isEditing ? (
                  <div className="flex-1 flex gap-2 items-start">
                    <input className="w-28 bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 text-xs font-medium" placeholder="Field" value={ref.field} onChange={(e) => updateReference(origIdx, 'field', e.target.value)} />
                    <input className="flex-1 bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 text-xs" placeholder="Source" value={ref.source} onChange={(e) => updateReference(origIdx, 'source', e.target.value)} />
                    <input className="w-40 bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 text-xs" style={{ color: roleColor(theme, 'accent') }} placeholder="URL (optional)" value={ref.url || ''} onChange={(e) => updateReference(origIdx, 'url', e.target.value)} />
                    <button onClick={() => removeReference(origIdx)} className="text-danger hover:text-danger shrink-0"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <div className="flex-1">
                    {/* AP1 — `ref.field` is the raw provider key
                       (e.g. `climate`, `siteResearch`). Route through
                       `formatReferenceCategory` so the appendix shows
                       human-readable category labels. `ref.source` is
                       stripped of the ` (cached)` suffix the provider
                       cache layer appends — that's internal status,
                       not reviewer-relevant. */}
                    <span className="font-medium" style={{ color: roleColor(theme, 'ink') }} data-testid={`ref-category-${origIdx + 1}`}>
                      {isRetiredSimulationDataSource(ref)
                        ? 'Simulation evidence'
                        : formatReferenceCategory(ref.field)}
                    </span>
                    <span className="mx-1">–</span>
                    {/* BUG #13 — the row container baseline is the faint
                        `text-text-secondary` grey; the source description used to
                        inherit it and read too pale. Pin it to the legible body
                        ink (`roleColor(theme,'body')`), matching the field-name
                        ink contrast and the rest of the report body. */}
                    <span
                      style={{ color: roleColor(theme, 'body') }}
                      data-retired-simulation-reference={isRetiredSimulationDataSource(ref) ? 'true' : undefined}
                    >
                      {isRetiredSimulationDataSource(ref)
                        ? LEGACY_SIMULATION_REFERENCE_LABEL
                        : ref.field === 'benchmark' && (citedIn.get(origIdx + 1) ?? []).length === 0
                          ? `${stripCachedSuffix(ref.source)} (generic catalog entry only – numerical LCOE bands omitted from this report pending exact table/page, dollar-year, and site boundary verification)`
                          : stripCachedSuffix(ref.source)}
                    </span>
                    {/* AP3 — URL renders as hostname-only hyperlink with
                       a decorative vector external-link mark (or full ADAMS accession URL
                       synthesized for bare accessions). AP6 — ISO date
                       `YYYY-MM-DD` for the retrieved column. */}
                    {(() => {
                      if (isRetiredSimulationDataSource(ref)) {
                        return (
                          <span
                            className="block text-[10px] mt-0.5"
                            style={{ color: roleColor(theme, 'muted') }}
                            data-testid={`ref-retired-${origIdx + 1}`}
                          >
                            {LEGACY_SIMULATION_REGEN_GUIDANCE}
                          </span>
                        );
                      }
                      const explicitUrl = ref.url && !/^doc:\/\//i.test(ref.url.trim()) ? ref.url : undefined;
                      const adamsAccession = isAdamsAccession(ref.field) ? ref.field
                        : (ref.source.match(/\b(ML\d{6,})\b/i)?.[1] ?? null);
                      const resolvedUrl = explicitUrl
                        ?? (adamsAccession ? adamsUrlForAccession(adamsAccession) : null);
                      if (!resolvedUrl) {
                        return (
                          // BUG #13 — pin the retrieved-date to body ink instead of
                          // the faint `text-text-secondary` grey it inherited from
                          // the row container.
                          <span className="ml-2" style={{ color: roleColor(theme, 'body') }} data-testid={`ref-retrieved-${origIdx + 1}`}>
                            · retrieved {formatIsoDate(ref.retrievedAt)}
                          </span>
                        );
                      }
                      return (
                        <>
                          <span className="mx-1">·</span>
                          <a
                            href={resolvedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                            style={{ color: roleColor(theme, 'accent') }}
                            data-testid={`ref-link-${origIdx + 1}`}
                          >
                            {hostnameOf(resolvedUrl)}{' '}
                            <ArrowUpRight
                              aria-hidden="true"
                              data-pdf-external-link-mark="true"
                              className="inline h-[0.8em] w-[0.8em] align-baseline"
                              strokeWidth={1.75}
                            />
                          </a>
                          {/* BUG #13 — body ink for legible retrieved-date (was the
                              faint `text-text-secondary` inherited from the row). */}
                          <span className="ml-2" style={{ color: roleColor(theme, 'body') }} data-testid={`ref-retrieved-${origIdx + 1}`}>
                            · retrieved {formatIsoDate(ref.retrievedAt)}
                          </span>
                        </>
                      );
                    })()}
                    {/* AP6 — per-reference back-references. The body
                       narrative was walked once at row-list build
                       time; here we just look up this row's section
                       labels and render them as a "Cited in X, Y"
                       strip. */}
                    {(() => {
                      const sectionsCited = citedIn.get(origIdx + 1) ?? [];
                      if (sectionsCited.length === 0) return null;
                      return (
                        <div
                          className="text-[10px] text-text-muted mt-0.5"
                          data-testid={`ref-cited-in-${origIdx + 1}`}
                        >
                          Cited in {sectionsCited.join(', ')}
                        </div>
                      );
                    })()}
                    {/* REF-LINEAGE REF-4 — when the reference is a
                       doc-extracted claim with a recorded excerpt,
                       surface the verbatim quote inline so reviewers
                       can sanity-check the extraction without opening
                       the viewer. Sourced via the same
                       resolveDocumentLineage helper used by RefLink
                       (REF-3) so the row preview and the [Ref N]
                       click-through describe the same passage.
                       Skipped silently when `sed` isn't provided
                       (back-compat with legacy callers / print export). */}
                    {(() => {
                      if (isRetiredSimulationDataSource(ref)) return null;
                      const lineage = resolveDocumentLineage(ref, sed);
                      if (!lineage || !lineage.excerptPreview) return null;
                      const heading = lineage.sectionHeading ? `${lineage.sectionHeading} · ` : '';
                      return (
                        <div
                          className="text-[10px] italic mt-1"
                          // William QA 2026-06-28 — left accent rule removed report-wide
                          style={{ color: roleColor(theme, 'body') }}
                          data-testid={`ref-excerpt-${origIdx + 1}`}
                        >
                          &ldquo;{lineage.excerptPreview}&rdquo;
                          {(heading || lineage.pageNumber) && (
                            <span className="not-italic text-text-muted ml-1">
                              – {heading}{lineage.pageNumber ? `page ${lineage.pageNumber}` : ''}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    {!isRetiredSimulationDataSource(ref) && ref.subItems && ref.subItems.length > 0 && (
                      <details
                        className="mt-1.5"
                        // A closed disclosure's children can be painted via CSS,
                        // but they do not contribute to the <details> box height.
                        // That left recovered immutable/PDF appendix pages unable
                        // to measure or split long topic lists. Export artifacts
                        // require the complete evidence register, so keep the
                        // disclosure semantically open before pagination.
                        open={audienceMode === 'export' || immutableArtifactMode}
                      >
                        <summary className="text-[11px] text-text-secondary cursor-pointer select-none hover:text-text-primary">
                          Topics ({ref.subItems.length})
                        </summary>
                        <ul className="mt-1.5 space-y-1 pl-4 list-disc text-[11px] text-text-secondary">
                          {ref.subItems.map((item, j) => (
                            <li key={j}>
                              <span className="font-medium text-text-primary">{item.topic}:</span>{' '}
                              <span>{item.detail}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            );

            // #13 (William 2026-06-28) — render the cited refs as ONE FLAT register
            // in ASCENDING [Ref N] order, published AND editing (citedRefs is already
            // in origIdx order). The prior AP2 4-bucket grouping (Provider / Knowledge
            // Base / Regulatory / Uploaded) reordered the [Ref N] numbers within each
            // bucket so they read out-of-sequence in the appendix; a flat ascending
            // register is what reviewers expect.

            return (
              <div id="dataSources" data-section="dataSources" className="space-y-4" style={{ scrollMarginTop: '80px' }}>
                {/* BUG #12 — contextual sub-heading for the grouped references
                    block. The appendix opener (h2 "Appendix & References", owned by
                    the editorial shell in flag-ON mode / the caller-owned h2 in
                    flag-OFF mode) titles the WHOLE appendix; the grouped
                    "Provider / Knowledge Base / Regulatory / Uploaded" register
                    underneath had no contextual heading, leaving it orphaned next
                    to its labelled siblings ("Data quality notes", "Documents",
                    "Recorded source context"). This h3 sub-heading matches those
                    siblings' style exactly (appendixHeadStyle accent ink) and is a
                    sub-heading DISTINCT from the appendix opener, so it never
                    duplicates the shell's appendix h2 in either flag state. Hidden
                    in edit mode — the editor shows the addressable flat list +
                    Add-Reference affordance, not the published register. */}
                {!isEditing && (
                  <h3 className="text-sm font-bold uppercase tracking-widest" style={appendixHeadStyle(theme)}>
                    Data Sources &amp; References
                  </h3>
                )}
                <div className="space-y-2" data-testid="references-flat">
                  {citedRefs.map(renderRow)}
                </div>
                {isEditing && (
                  <Button variant="outline" size="sm" className="h-8 rounded-none border-dashed border-primary/30 text-primary/60 hover:bg-primary/5 uppercase tracking-widest text-[9px]"
                    onClick={addReference}>
                    <Plus className="w-3 h-3 mr-1" /> Add Reference
                  </Button>
                )}
              </div>
            );
          })()}

          {/* Data quality notes — Content-Source Fixes Prompt 6.
             Cross-validation flags surface here, NOT inside the
             references list. They're metadata about the data, not
             citable sources. The previous render emitted them as a
             rollup `[Ref N]` row which both bloated the appendix and
             invited the AI to cite them. */}
          {(() => {
            const findings = report.crossValidationFindings ?? [];
            if (findings.length === 0) return null;
            return (
              <div id="dataQualityNotes" data-section="dataQualityNotes" data-testid="data-quality-notes" className="space-y-3 pt-8" style={{ scrollMarginTop: '80px', borderTop: `1px solid ${roleColor(theme, 'hair')}` }}>
                <h3 className="text-sm font-bold uppercase tracking-widest" style={appendixHeadStyle(theme)}>Data quality notes</h3>
                <p className="text-[11px]" style={{ color: roleColor(theme, 'body') }}>
                  Cross-validation flags raised during evidence
                  compilation. These are integrity signals, not
                  citations, and reflect tensions between sources
                  rather than authoritative claims.
                </p>
                <ul className="space-y-1.5 text-xs" style={{ color: roleColor(theme, 'body') }}>
                  {findings.map((f, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="shrink-0" style={{ fontFamily: MONO, color: roleColor(theme, f.severity === 'error' ? 'negative' : 'warning') }}>
                        {f.severity.toUpperCase()}
                      </span>
                      <span>{f.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {/* Documents — relocated from above the appendix.
             Two panels grouped under one heading:
               1. Documents informing this report (per-ref audit trail
                  — included/excluded + relevance bar + reason),
               2. Coverage checklist (canonical taxonomy for this
                  report type with the satisfying docs inline).
             The standalone "Supporting Documents" block was removed:
             it duplicated rows already in (1) using a fuzzy filename
             match against the narrative — same docs, less precision. */}
          {(() => {
            const refs = report.documentRefs ?? [];
            const reportType = (report.reportType ?? 'feasibility') as import('../../../config/reportTypes').ReportTypeId;
            const docs = getReportDocuments(report, site);
            // Bail when there's no signal to show — neither audit refs
            // nor a populated doc set.
            if (refs.length === 0 && docs.length === 0) return null;

            // DDL-FOLLOWUP-IDREF — reads through resolveSiteDocuments for fresh state.
            // The by-id map prefers the live `site.documents` so that
            // post-gen mutations (rename, importance recompute, soft-
            // delete) flow into the appendix without a report-side write.
            // The fallback to `report.documents` keeps pre-IDREF reports
            // rendering. Note: `resolveSiteDocuments(site, report)` is
            // computed lazily only when no canonical site library exists,
            // so the common path stays O(N) on `site.documents.length`.
            const docsById = new Map(
              (site?.documents ?? resolveSiteDocuments(site, report)).map((d, idx) => [
                d.id,
                withResolvedSiteDocumentFileName(d, `doc-${idx}`),
              ])
            );
            // DRC-2 — canonical stats bundle for every headline count on
            // this surface. `computeReportDocStats` is the single source
            // of truth shared with the Reports tab row chip, the
            // (future) report header chip, and the Site card. The
            // `dedupeRevisions` flag is passed through so the
            // visibleLatestCount / hiddenRevisionsCount stays consistent
            // with the appendix's dedupe banner toggle.
            const stats = computeReportDocStats(report, site, { dedupeRevisions, sed });
            // DRC-2 — split included refs into TWO buckets that used to
            // collide in the legacy "X included" tally:
            //   includedAndLiveRefs  — userIncluded === true AND the doc
            //                          still resolves in live site.documents.
            //                          These are the rows the appendix actually
            //                          renders.
            //   droppedAsMissingRefs — userIncluded === true BUT the doc was
            //                          deleted from the site after the report
            //                          ran. Previously folded silently into
            //                          "excluded" (4 excluded), eroding trust
            //                          when the user knew they'd excluded only
            //                          one doc. Now surfaced as its own count
            //                          chip — no ghost rows in the body.
            // `excludedRefs` keeps its old semantics — userIncluded === false
            // with `reasonIfExcluded`. The doc may or may not still resolve;
            // the user's "no" stands either way.
            const relevanceSortKey = (r: typeof refs[number]) =>
              typeof r.relevance === 'number' && Number.isFinite(r.relevance) ? r.relevance : 0;
            const includedAndLiveRefs = refs
              .filter(r => r.userIncluded && docsById.has(r.docId))
              .sort((a, b) => relevanceSortKey(b) - relevanceSortKey(a));
            const droppedAsMissingRefs = refs
              .filter(r => r.userIncluded && !docsById.has(r.docId));
            const excludedRefs = refs
              .filter(r => !r.userIncluded)
              .sort((a, b) => relevanceSortKey(b) - relevanceSortKey(a));
            // RCO-2 — within the live subset, partition by whether the
            // narrative actually cited the docId. Headline counts pivot
            // to `citedRefs.length` so the appendix stops overpromising.
            // `citedDocIds` is empty for legacy reports with no [Ref N]
            // tokens AND no claim-sources telemetry — that correctly
            // folds every includedAndLiveRef into "provided but not cited",
            // signalling "no citation telemetry available" rather than
            // claiming every doc was leaned on.
            //
            // DRC-2 composition note: `includedAndLiveRefs` (DRC) feeds
            // RCO's cited/uncited split. A ref whose docId was deleted
            // from the site won't appear in either bucket — it's
            // surfaced via the `droppedAsMissingRefs` count chip
            // instead, which is honest about what happened.
            const citedRefs = includedAndLiveRefs.filter(r => citedDocIds.has(r.docId));
            const providedUncitedRefs = includedAndLiveRefs.filter(r => !citedDocIds.has(r.docId));
            const hasCitationTelemetry =
              citedDocIds.size > 0
              || stats.knowledgeBaseCitedCount > 0
              || stats.inlineFilenameCitedCount > 0;

            return (
              <div id="appendix-documents" className="space-y-6 pt-4" data-testid="appendix-documents" style={{ scrollMarginTop: '80px', borderTop: `1px solid ${roleColor(theme, 'hair')}` }}>
                <h3 className="text-sm font-bold uppercase tracking-widest" style={appendixHeadStyle(theme)}>
                  Documents
                </h3>

                {refs.length > 0 && (() => {
                  // RCO-2 — Build the deduped projection ONCE over the
                  // union of all three buckets (cited / provided-uncited
                  // / excluded). Revision dedupe is global: an earlier
                  // revision of a cited doc should never resurface in
                  // the Provided-uncited bucket just because the user
                  // also dropped it into the prompt context. The leader
                  // mapping is shared, so per-bucket rendering can ask
                  // "which group does this docId belong to?" and skip
                  // the row when a later bucket has already shown the
                  // leader.
                  const orderedRefs = [...citedRefs, ...providedUncitedRefs, ...excludedRefs];
                  const referencedDocs = orderedRefs
                    .map(r => docsById.get(r.docId))
                    .filter((d): d is SiteDocument => Boolean(d));
                  const groups = dedupeDocsByCanonicalName(referencedDocs);
                  const groupByDocId = new Map<string, DedupedDocGroup>();
                  for (const g of groups) {
                    groupByDocId.set(g.latest.id, g);
                    for (const r of g.revisionsHidden) groupByDocId.set(r.id, g);
                  }
                  const totalHidden = countHiddenRevisions(groups);

                  // Per-bucket walk: pre-sorted order, one row per
                  // group on its first appearance WITHIN the bucket.
                  // The seen-set is bucket-local so each bucket renders
                  // its own leaders; if the same docId somehow appears
                  // in two buckets (shouldn't — citedDocIds partitions
                  // disjointly), each bucket gets its own row.
                  type DedupedRow = { ref: typeof refs[number]; group: DedupedDocGroup | null };
                  const dedupeBucket = (bucket: typeof refs): DedupedRow[] => {
                    const seenGroupLeaders = new Set<string>();
                    return bucket.flatMap((ref): DedupedRow[] => {
                      if (!dedupeRevisions) return [{ ref, group: null }];
                      const group = groupByDocId.get(ref.docId);
                      if (!group) return [{ ref, group: null }];
                      if (seenGroupLeaders.has(group.latest.id)) return [];
                      seenGroupLeaders.add(group.latest.id);
                      return [{ ref, group }];
                    });
                  };
                  const citedRows = dedupeBucket(citedRefs);
                  const providedUncitedRows = dedupeBucket(providedUncitedRefs);
                  const excludedRows = dedupeBucket(excludedRefs);

                  // Single row renderer reused across all three buckets.
                  const renderRow = ({ ref, group }: DedupedRow) => {
                    const displayDoc = (dedupeRevisions && group) ? group.latest : docsById.get(ref.docId);
                    const rawName = displayDoc ? resolveSiteDocumentFileName(displayDoc, ref.docId) : `(missing doc ${ref.docId.slice(0, 8)}…)`;
                    const fileName = displayDoc ? cleanDocFileName(rawName) : rawName;
                    const summary = reportDocumentPreview(displayDoc, sed, report.sedId);
                    const hidden = group?.revisionsHidden ?? [];
                    const expandKey = group ? group.latest.id : ref.docId;
                    const expanded = expandedGroups.has(expandKey);
                    const tier = documentRelevanceTier(ref.relevance);
                    return (
                      <li
                        key={ref.docId}
                        className="grid grid-cols-[10px_1fr_auto] items-start gap-2 text-[10px]"
                        data-testid={`appendix-doc-row-${expandKey}`}
                      >
                        <span
                          className="mt-1 inline-block w-2 h-2 shrink-0"
                          style={{ background: roleColor(theme, ref.userIncluded ? 'positive' : 'faint') }}
                          aria-label={ref.userIncluded ? 'Included' : 'Excluded'}
                        />
                        <div className="min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-semibold truncate" style={{ color: roleColor(theme, 'ink') }} title={rawName}>{fileName}</span>
                            {displayDoc?.priority && (
                              <span
                                title="Priority – deep analysis"
                                className="inline-flex shrink-0"
                              >
                                <Star
                                  role="img"
                                  aria-label="Priority – deep analysis"
                                  data-pdf-priority-document-mark="true"
                                  className="h-[0.9em] w-[0.9em]"
                                  style={{ color: roleColor(theme, 'accent') }}
                                  fill="currentColor"
                                  strokeWidth={2}
                                />
                              </span>
                            )}
                            {dedupeRevisions && hidden.length > 0 && (
                              <button
                                type="button"
                                onClick={() => toggleGroup(expandKey)}
                                className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-border text-text-muted hover:text-text-primary hover:border-border-strong"
                                title={`This document has ${hidden.length} earlier revision${hidden.length === 1 ? '' : 's'}`}
                                data-testid={`appendix-doc-revisions-toggle-${expandKey}`}
                              >
                                +{hidden.length} earlier revision{hidden.length === 1 ? '' : 's'}
                              </button>
                            )}
                          </div>
                          {summary && (
                            <div className="text-[10px] truncate" style={{ color: roleColor(theme, 'muted') }}>{summary}</div>
                          )}
                          {!ref.userIncluded && ref.reasonIfExcluded && (
                            <div className="text-[9px] italic mt-0.5" style={{ color: roleColor(theme, 'warning') }}>
                              Excluded – {ref.reasonIfExcluded}
                            </div>
                          )}
                          {dedupeRevisions && expanded && hidden.length > 0 && (
                            <ul
                              className="mt-1 space-y-0.5 pl-2 border-l border-border"
                              data-testid={`appendix-doc-revisions-${expandKey}`}
                            >
                              {hidden.map(rev => (
                                <li key={rev.id} className="text-[10px] text-text-muted truncate">
                                  <span className="mr-1" style={{ fontFamily: MONO }}>↳</span>
                                  {cleanDocFileName(rev.fileName)}
                                  {rev.uploadedAt && (
                                    <span className="ml-1 text-text-muted">
                                      · {formatIsoDate(rev.uploadedAt)}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {/* AP4 — tier chip in place of the relevance bar.
                          Numeric score is preserved in the title
                          attribute for tooltip reveal. */}
                        <span
                          className={`flex items-center gap-1 shrink-0 text-[10px] uppercase tracking-widest font-bold ${tier.toneClass}`}
                          title={`Relevance ${typeof ref.relevance === 'number' && Number.isFinite(ref.relevance) ? ref.relevance.toFixed(2) : '–'} for this report type`}
                          data-testid={`appendix-doc-tier-${expandKey}`}
                          data-tier={tier.tier}
                        >
                          <DocumentRelevanceMark tier={tier.tier} />
                          {tier.label}
                        </span>
                      </li>
                    );
                  };

                  // Render one bucket's rows grouped by path-derived
                  // category. Each category renders as <details open>
                  // so the appendix stays scannable. Empty bucket → null
                  // so the parent can decide whether to render the
                  // surrounding <details> wrapper at all.
                  //
                  // The Cited bucket (and the no-telemetry fallback)
                  // pass `containerTestid = 'documents-grouped-by-category'`
                  // + `categoryTestidPrefix = 'appendix-doc-category'`
                  // so the legacy AP4 regression suite (which scans for
                  // `appendix-doc-category-<slug>`) keeps passing. The
                  // Provided-uncited and Excluded buckets get their own
                  // namespace so the same Site-Control slug doesn't
                  // collide across buckets in a single render tree.
                  const renderBucketByCategory = (
                    rows: DedupedRow[],
                    containerTestid: string,
                    categoryTestidPrefix: string,
                  ): React.ReactNode => {
                    if (rows.length === 0) return null;
                    const byCategory = new Map<DocumentCategory, DedupedRow[]>();
                    for (const row of rows) {
                      const displayDoc = (dedupeRevisions && row.group) ? row.group.latest : docsById.get(row.ref.docId);
                      const path = displayDoc?.fileName ?? '';
                      const category = documentCategoryFromPath(path);
                      const bucket = byCategory.get(category) ?? [];
                      bucket.push(row);
                      byCategory.set(category, bucket);
                    }
                    return (
                      <div className="space-y-3" data-testid={containerTestid}>
                        {DOCUMENT_CATEGORY_ORDER.map(category => {
                          const catRows = byCategory.get(category) ?? [];
                          if (catRows.length === 0) return null;
                          return (
                            <details
                              key={category}
                              open
                              data-testid={`${categoryTestidPrefix}-${category.replace(/[^A-Za-z0-9]+/g, '-')}`}
                            >
                              <summary className="cursor-pointer select-none text-[10px] font-bold uppercase tracking-widest" style={{ fontFamily: MONO, letterSpacing: '0.14em', color: roleColor(theme, 'ink') }}>
                                {DOCUMENT_CATEGORY_LABEL[category]}
                                <span className="ml-2 text-text-muted font-normal normal-case">({catRows.length})</span>
                              </summary>
                              <ul className="space-y-1.5 pt-1.5">
                                {catRows.map(renderRow)}
                              </ul>
                            </details>
                          );
                        })}
                      </div>
                    );
                  };

                  return (
                    <div className="space-y-3" data-testid="documents-informing-this-report">
                      {/* RCO-2 — headline counts cited-FACT, not
                         included-INTENT. The "Provided but not cited"
                         and "Excluded by user" chips fall out as
                         secondary counters so the reader can still
                         see the full prompt-context footprint without
                         misreading the lead number. When citation
                         telemetry is empty (legacy report with no
                         [Ref N] and no claims) we show "no citation
                         telemetry" instead of "0 cited" — the latter
                         would imply we measured zero when we actually
                         measured nothing.

                         DRC-2 — adds two honest chips that the legacy
                         "X included · Y excluded" formulation hid:
                           - "N deleted from site since report ran"
                             surfaces refs whose docId no longer
                             resolves in live site.documents. Previously
                             folded into the misleading "excluded" tally.
                           - "of M site total today" closes the loop
                             on the "85 vs 86 vs 73" three-number
                             puzzle — the user can see at a glance how
                             the report's frozen-at-gen footprint
                             relates to the live site library.
                         The DRC-3 testid prefix `report-doc-count-*`
                         lets the cross-surface invariant test (Reports
                         tab row chip = subhead headline = future header
                         chip) assert numerical agreement. */}
                      <div className="flex items-center justify-between">
                        <h4 className="text-[11px] font-bold uppercase tracking-widest" style={{ fontFamily: MONO, letterSpacing: '0.14em', color: roleColor(theme, 'accent') }}>
                          Documents provided for this report
                          <span className="ml-2 text-text-muted font-normal normal-case">
                            – {citedRefs.length > 0 ? (
                              <>
                                <span data-testid="report-doc-count-cited">{`${citedRefs.length} cited`}</span>
                                {stats.knowledgeBaseCitedCount > 0 && (
                                  <>
                                    <span aria-hidden> · </span>
                                    <span data-testid="report-doc-count-kb-cited">
                                      {`${stats.knowledgeBaseCitedCount} kb-cited`}
                                    </span>
                                  </>
                                )}
                                {stats.inlineFilenameCitedCount > 0 && (
                                  <>
                                    <span aria-hidden> · </span>
                                    <span data-testid="report-doc-count-inline-cited">
                                      {`${stats.inlineFilenameCitedCount} inline-cited`}
                                    </span>
                                  </>
                                )}
                              </>
                            ) : stats.knowledgeBaseCitedCount > 0 ? (
                              <>
                                <span data-testid="report-doc-count-kb-only">
                                  {`${stats.knowledgeBaseCitedCount} kb-cited (no user-doc citations)`}
                                </span>
                                {stats.inlineFilenameCitedCount > 0 && (
                                  <>
                                    <span aria-hidden> · </span>
                                    <span data-testid="report-doc-count-inline-cited">
                                      {`${stats.inlineFilenameCitedCount} inline-cited`}
                                    </span>
                                  </>
                                )}
                              </>
                            ) : stats.inlineFilenameCitedCount > 0 ? (
                              <span data-testid="report-doc-count-inline-cited">
                                {`${stats.inlineFilenameCitedCount} inline-cited`}
                              </span>
                            ) : (
                              <>
                                <span data-testid="report-doc-count-no-telemetry" title="No [Ref N] or claim-source telemetry on this report">
                                  no citation telemetry
                                </span>
                                <span aria-hidden> · </span>
                              </>
                            )}
                            <span
                              data-testid="report-doc-count-informing"
                              data-informing-count={stats.includedAndLive}
                              className="sr-only"
                              aria-hidden
                            >
                              {stats.includedAndLive}
                            </span>
                            {/* "Provided but not cited" is meaningful
                               only when there IS citation telemetry —
                               otherwise we can't claim the docs were
                               *not* cited (we just didn't measure).
                               Suppress the chip in the no-telemetry
                               case so the headline stays honest. */}
                            {hasCitationTelemetry && providedUncitedRefs.length > 0 && (
                              <>
                                {' · '}
                                <span data-testid="report-doc-count-provided-uncited">
                                  {`${providedUncitedRefs.length} provided but not cited`}
                                </span>
                              </>
                            )}
                            {/* User-excluded count chip intentionally removed —
                               reports must not surface "N excluded by user" in
                               their published output (user feedback: not
                               useful to readers, distracts from the included
                               evidence). The excluded set is still tracked
                               internally for audit + rerun flows. */}
                            {/* DRC-2 — silently-deleted refs. Suppressed
                               when zero so a clean appendix stays clean. */}
                            {stats.droppedAsMissing > 0 && (
                              <>
                                {' · '}
                                <span
                                  data-testid="report-doc-count-deleted-since-gen"
                                  title="These documents were included at generation time but have since been deleted from the site library."
                                >
                                  {`${stats.droppedAsMissing} deleted from site since report ran`}
                                </span>
                              </>
                            )}
                            {/* DRC-2 — "of N site total today" tail.
                               Reconciles the report's frozen-at-gen
                               footprint with the live site library
                               size (the Site card / Documents tab
                               number). Suppressed when both totals
                               are zero — nothing to reconcile. */}
                            {stats.siteTotalLive > 0 && (
                              <>
                                {' · '}
                                <span
                                  className="text-text-secondary"
                                  data-testid="report-doc-count-site-total-live"
                                  title="Live site library size today (excluding the auto-generated library index)."
                                >
                                  {`of ${stats.siteTotalLive} site total today`}
                                </span>
                              </>
                            )}
                          </span>
                        </h4>
                        {report.documentSelectionMode && (
                          // N1.5C-flat — in editorial mode the doc-selection badge
                          // border reads the theme `hair` hairline (theme-aware on
                          // cream) + muted ink, instead of the app `border-border`/
                          // `text-text-muted` tokens. Flag OFF keeps the exact
                          // legacy className + no inline style (byte-identical).
                          <span
                            className={
                              editorialActive
                                ? 'text-[10px] uppercase tracking-widest px-1.5 py-0.5 border'
                                : 'text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-border text-text-muted'
                            }
                            style={
                              editorialActive
                                ? { borderColor: roleColor(theme, 'hair'), color: roleColor(theme, 'muted') }
                                : undefined
                            }
                          >
                            {report.documentSelectionMode === 'auto'
                              ? 'AI auto-pick'
                              : report.documentSelectionMode === 'mixed'
                                ? 'Mixed'
                                : 'Manual'}
                          </span>
                        )}
                      </div>

                      {/* Dedupe banner — surfaces hidden-revision count + a
                         toggle. Only renders when there's actually
                         something hidden so a clean appendix stays
                         clean. Suppressed in export so customer PDFs
                         don't carry the admin tooling. */}
                      {audienceMode !== 'export' && totalHidden > 0 && (
                        <div
                          className="flex items-center justify-between text-[10px] px-2 py-1"
                          // FLATTEN-TO-CREAM — drop the grey `subtleBg` fill on this
                          // (screen-only, export-suppressed) revisions banner; the
                          // hairline border alone delineates it on the cream page.
                          style={{ color: roleColor(theme, 'muted'), background: 'transparent', border: `1px solid ${roleColor(theme, 'hair')}` }}
                          data-testid="documents-dedupe-banner"
                        >
                          <span>
                            {dedupeRevisions
                              ? `Showing ${groups.length} latest document${groups.length === 1 ? '' : 's'} · ${totalHidden} earlier revision${totalHidden === 1 ? '' : 's'} hidden`
                              : `Showing all ${referencedDocs.length} documents (revisions visible)`}
                          </span>
                          <button
                            type="button"
                            onClick={() => setDedupeRevisions(v => !v)}
                            className="font-bold uppercase tracking-widest text-text-secondary hover:text-text-primary underline"
                            data-testid="documents-dedupe-toggle"
                          >
                            {dedupeRevisions ? 'Show all' : 'Collapse revisions'}
                          </button>
                        </div>
                      )}

                      {/* RCO-2 — Cited bucket (default open).
                         These are the docs whose docId actually
                         appears as a [Ref N] resolved through SED
                         lineage or as a doc:<id> in a ReportClaim
                         source. AP4 path-derived category grouping
                         applies WITHIN this bucket; tier chips and
                         revision-dedupe behaviour are unchanged. */}
                      {hasCitationTelemetry && citedRows.length > 0 && (
                        <div className="space-y-2" data-testid="documents-cited-section">
                          {renderBucketByCategory(citedRows, 'documents-grouped-by-category', 'appendix-doc-category')}
                        </div>
                      )}
                      {/* RCO-2 — Legacy fallback: when there's no
                         citation telemetry at all, we don't know
                         which docs were cited. Render the full
                         includedRefs list under the same legacy
                         testids so AP4's regression suite + every
                         consumer that scans for the grouped list
                         keeps working. The "Provided but not cited"
                         disclosure is suppressed in this mode (it
                         would imply we measured them when we didn't). */}
                      {!hasCitationTelemetry && citedRows.length === 0 && providedUncitedRows.length > 0 && (
                        <div className="space-y-2" data-testid="documents-no-telemetry-fallback">
                          {renderBucketByCategory(providedUncitedRows, 'documents-grouped-by-category', 'appendix-doc-category')}
                        </div>
                      )}

                      {/* Inclusion records the saved input selection, not model
                         review or prompt delivery. This also applies to reports
                         constructed deterministically from the SED. Suppress this
                         bucket when citation telemetry is unavailable. */}
                      {hasCitationTelemetry && providedUncitedRows.length > 0 && (
                        <details
                          open={providedUncitedOpen}
                          onToggle={(e) => setProvidedUncitedOpen((e.target as HTMLDetailsElement).open)}
                          data-testid="documents-provided-uncited-section"
                        >
                          <summary className="cursor-pointer select-none text-[11px] font-bold uppercase tracking-widest" style={{ fontFamily: MONO, letterSpacing: '0.14em', color: roleColor(theme, 'muted') }}>
                            Provided but not cited
                            <span className="ml-2 text-text-muted font-normal normal-case">
                              ({providedUncitedRefs.length})
                            </span>
                          </summary>
                          <p className="text-[10px] text-text-muted italic mt-1 mb-2">
                            These documents were retained with the report inputs, but no
                            direct narrative citation was recorded. Inclusion does not
                            establish that a model reviewed their contents.
                          </p>
                          {renderBucketByCategory(providedUncitedRows, 'documents-provided-uncited-grouped', 'documents-provided-uncited-category')}
                        </details>
                      )}

                      {/* RCO-2 — Excluded by user (collapsed).
                         Docs the user explicitly toggled OFF in the
                         rerun wizard. Mirrors the existing pattern
                         but is now wrapped in its own disclosure
                         rather than mixed into the main grouped
                         list — keeps the appendix focused on
                         documents that actually carried weight. */}
                      {/* "Excluded by user" disclosure removed from published
                         reports — the documents the user toggled OFF in the
                         rerun wizard are tracked internally (for diff /
                         audit / rerun reproducibility) but no longer
                         surfaced in the report appendix. The published
                         appendix lists only documents that actually carried
                         weight in the analysis. */}
                    </div>
                  );
                })()}

                {/* DM1 — Coverage-checklist panel hard-removed from report
                   output (HTML + headless-PDF). The underlying matcher
                   (`matchUploadedDocsToChecklist`) is pure tag-pattern
                   matching with no relevance threshold and no Primary
                   exclusivity, so a single site-control doc was landing
                   as Primary evidence for 5+ unrelated checklist slots
                   (Geotech, Utility, Title, Zoning, Flood) in the same
                   report — credibility-destroying. DM2-DM8 replace the
                   matcher with content-aware ranking; until then this
                   panel is replaced by a breadcrumb pointing readers to
                   the Site Evidence tab, which still shows the matches
                   (and from DM5 onward lets the user Disconnect bad
                   ones). The matcher itself stays in place because
                   readiness, Path to GO, evidence lineage, section
                   provenance and the report wizard still call it — only
                   the report-output call site here is removed. */}
                {(() => {
                  // DRC-2 — route through the canonical helper so the
                  // breadcrumb's "N documents reviewed" number agrees
                  // with the headline subhead above. The pre-DRC formula
                  // `report.documents?.length ?? site?.documents?.length`
                  // was a stale union that could read the legacy
                  // inline `report.documents` length (snapshot-at-gen)
                  // OR the live site library (union surface) depending
                  // on which was populated — yet another count that
                  // could disagree with the subhead. Pinning to
                  // `includedAndLive` makes the breadcrumb's number
                  // identical to the headline `cited + provided` total.
                  const reportDocCount = stats.includedAndLive;
                  if (reportDocCount === 0) return null;
                  return (
                    <div
                      className="space-y-1 pt-3"
                      style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}
                      data-testid="appendix-doc-checklist-breadcrumb"
                    >
                      <h4 className="text-[11px] font-bold uppercase tracking-widest" style={appendixHeadStyle(theme)}>
                        Coverage
                      </h4>
                      <p className="text-[11px]" style={{ color: roleColor(theme, 'muted') }}>
                        <span data-testid="appendix-coverage-doc-count">{reportDocCount}</span> document{reportDocCount === 1 ? '' : 's'} provided · see Site Evidence tab for per-document detail.
                      </p>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          {/* Recorded site fields do not establish which provider APIs ran.
             Preserve the admin/export gate while labeling only saved context. */}
          {!isAdmin && audienceMode === 'public-share' && (
            <div
              className="space-y-2 pt-4"
              style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}
              data-testid="google-api-data-accessed-placeholder"
            >
              <h3 className="text-sm font-bold uppercase tracking-widest opacity-60" style={{ fontFamily: MONO, letterSpacing: '0.16em', color: roleColor(theme, 'muted') }}>
                Recorded source context
              </h3>
              <p className="text-[10px] uppercase tracking-widest opacity-60" style={{ fontFamily: MONO, letterSpacing: '0.16em', color: roleColor(theme, 'muted') }}>
                Admin-only block hidden in shared view
              </p>
            </div>
          )}
          {isAdmin && (() => {
            const elevationSources = (report.references ?? []).filter(ref => /elevation/i.test(ref.field));
            const elevRange = report.siteElevationRange as {
              min?: number;
              max?: number;
              avg?: number;
              pointsSampled?: number;
              cascadeSource?: string;
            } | null | undefined;
            const hasStoredBoundaryElevRange =
              elevRange != null &&
              typeof elevRange.min === 'number' &&
              typeof elevRange.max === 'number' &&
              (elevRange.min !== 0 || elevRange.max !== 0);
            const hasReliefProxyRef = (report.references ?? []).some(ref =>
              /elevation-relief/i.test(String(ref.source ?? '')),
            );
            const storedBoundaryElevNote = hasStoredBoundaryElevRange
              ? ` (Site Boundary displays the stored ${Math.round(elevRange.min!)}–${Math.round(elevRange.max!)} m ASL${typeof elevRange.pointsSampled === 'number' && elevRange.pointsSampled > 0 ? `, n=${elevRange.pointsSampled}` : ''} boundary polygon sample${elevRange.cascadeSource ? ` [${elevRange.cascadeSource}]` : ''}${hasReliefProxyRef ? '; Site Profile records the deterministic elevation-relief proxy' : ''})`
              : '';
            const apiRows: React.ReactNode[] = [
              <span key="elevation" data-testid="google-api-elevation">Recorded site elevation: {formatElevation((report.datasets?.land?.elevation as number | null | undefined) ?? null)}. {elevationSources.length > 0 ? `Recorded source: ${elevationSources.map(ref => ref.source).join('; ')}.` : hasStoredBoundaryElevRange ? `Report-level provider/API-call receipt not recorded${storedBoundaryElevNote}.` : 'Elevation provider is not recorded in this report.'}</span>,
              <span key="address">Recorded site address: {report.address || 'Not recorded'}. Geocoding provider and API calls are not established by this field.</span>,
            ];
            if (report.siteBoundary) {
              apiRows.push(<span key="boundary">Saved study boundary{Number.isFinite(report.siteArea) ? ` (${Math.round(report.siteArea!)} gross acres)` : ''}. Boundary capture method is not recorded in this report.</span>);
            }
            apiRows.push(<span key="calls">API call receipts and imagery requests are not recorded in this report. Saved site fields do not establish that Google Maps, Places, Drawing, or Satellite services were used.</span>);
            return (
              <div className="space-y-4 pt-4" style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }} data-testid="google-api-data-accessed-section">
                <h3 className="text-sm font-bold uppercase tracking-widest" style={appendixHeadStyle(theme)}>Recorded source context</h3>
                <div className="space-y-1 text-xs" style={{ color: roleColor(theme, 'body') }} data-testid="google-api-data-accessed">
                  {apiRows.map((row, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="shrink-0" style={{ fontFamily: MONO, color: roleColor(theme, 'accent') }}>[{String.fromCharCode(65 + i)}]</span>
                      {row}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Edit History — admin/editor surface only; suppressed in export. */}
          {showEditHistoryAppendix && report.editHistory && (report.editHistory?.length ?? 0) > 0 && (
            <div className="space-y-4 pt-4" style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}>
              <button
                className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest transition-colors"
                style={appendixHeadStyle(theme)}
                onClick={() => setShowEditHistory(!showEditHistory)}
              >
                <History className="w-4 h-4" />
                Edit History ({(report.editHistory?.length ?? 0)} corrections)
                {showEditHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {showEditHistory && (
                <div className="space-y-3">
                  {report.editHistory.map((edit, idx) => (
                    <div key={edit.id || idx} className="p-3 space-y-2" style={{ border: `1px solid ${roleColor(theme, 'hair')}` }}>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ fontFamily: MONO, letterSpacing: '0.14em', color: roleColor(theme, 'ink') }}>{edit.field}</span>
                        <span className="text-[9px]" style={{ color: roleColor(theme, 'muted') }}>{formatIsoDate(edit.editedAt)}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <span className="text-[8px] font-bold uppercase tracking-widest" style={{ fontFamily: MONO, color: roleColor(theme, 'negative') }}>Original</span>
                          <p className="text-[10px] mt-1 line-clamp-3" style={{ color: roleColor(theme, 'muted') }}>{edit.originalValue.slice(0, 200).replace(/^"|"$/g, '')}</p>
                        </div>
                        <div>
                          <span className="text-[8px] font-bold uppercase tracking-widest" style={{ fontFamily: MONO, color: roleColor(theme, 'positive') }}>Corrected</span>
                          <p className="text-[10px] mt-1 line-clamp-3" style={{ color: roleColor(theme, 'body') }}>{edit.editedValue.slice(0, 200).replace(/^"|"$/g, '')}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 22B Important-Notice block — the MAP pairs the numbered reference
             register with an Important-Notice. The references content above is
             ALREADY a token-driven numbered register: stable 1-based [Ref N]
             rows, grouped into reviewer buckets (Provider / Knowledge Base /
             Regulatory / Uploaded) with per-group counts, hostname links (AP3),
             back-references (AP6), excerpt previews (REF-4) and topic sub-items.
             It is NOT replaced by EditorialReferenceRegister: that primitive
             carries none of those features, and AP3/AP6/REF-4 + the cited-only
             filter + the edit-mode addressable flat list are preserved wiring
             pinned by the ReferencesSection.{appendix,RCO,DRC} suites — swapping
             it would regress, not port. So 22B composes the genuinely-missing,
             MAP-named additive primitive: the Important-Notice block as an
             EditorialCallout.

             // GAP: none on the data side (references carry source/date/
             // category). The FULL legal "Important Notice" (ReportLegalDisclaimer)
             // is owned by the renderer tail (ReportClosingTail) and must not be
             // duplicated here — this is the lighter appendix-scoped provenance
             // caveat that frames the register, gated to non-edit mode and to
             // appendices that actually carry references/documents. */}


          {reportHasStructuredActionPlanDetail(report.developmentPathway, report.recommendations) && (
            <div
              data-section="actionPlan"
              data-testid="action-plan-supporting-appendix"
              className="space-y-4 pt-4"
              style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}
            >
              <h3 className="text-sm font-bold uppercase tracking-widest" style={appendixHeadStyle(theme)}>
                Action Plan &amp; Pathway — Supporting Phase Water, Staffing &amp; Recommendation Detail
              </h3>
              <div className="space-y-3 text-xs" style={{ color: roleColor(theme, 'body') }}>
                {(report.developmentPathway ?? []).map((phase, idx) => {
                  const parsed = parseActionPlanPhaseDescription(phase.description);
                  if (!parsed.hasStructuredDisclosure || !parsed.supportingDisclosureText) return null;
                  return (
                    <div
                      key={`phase-appendix-${idx}`}
                      className="space-y-1.5"
                      style={{ borderLeft: `2px solid ${roleColor(theme, 'hair')}`, paddingLeft: 10 }}
                    >
                      <strong style={{ color: roleColor(theme, 'ink') }}>
                        {phase.phase ? `${phase.phase} — ${phase.title}` : phase.title}
                        {phase.cod ? ` (${phase.cod})` : ''}
                      </strong>
                      <div className="whitespace-pre-line">
                        {walkClaimsText(phase.description, report.references, report.claims)}
                      </div>
                    </div>
                  );
                })}
                {(report.recommendations ?? []).map((rec, idx) => {
                  const parsedRec = parseRecommendationForCard(rec, true, {
                    siteId: report.siteId,
                    reportId: report.id,
                    sedId: report.sedId,
                    references: report.references,
                    developmentPathway: report.developmentPathway,
                  });
                  if (!parsedRec.hasSupportingDetail && !rec.rationale?.trim()) return null;
                  return (
                    <div
                      key={`rec-appendix-${idx}`}
                      className="space-y-1"
                      style={{ borderLeft: `2px solid ${roleColor(theme, 'hair')}`, paddingLeft: 10 }}
                    >
                      <strong style={{ color: roleColor(theme, 'ink') }}>
                        Recommendation {idx + 1}: {rec.title} ({rec.type})
                      </strong>
                      {parsedRec.structuredMeta.length > 0 && (
                        <div style={{ color: roleColor(theme, 'muted') }}>
                          {parsedRec.structuredMeta.map((m) => `${m.label}: ${m.value}`).join(' · ')}
                        </div>
                      )}
                      {rec.description && (
                        <div>{walkClaimsText(rec.description, report.references, report.claims)}</div>
                      )}
                      {rec.rationale && (
                        <div>{walkClaimsText(rec.rationale, report.references, report.claims)}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* NEVER-HIDE fallback. With the self-hide gate removed, a report
             whose references[] / data-quality / documents are all empty would
             otherwise show a lone "Appendix" heading. `appendixHasContent`
             (kept solely for this purpose now) is false exactly when every
             child block above self-guarded to null, so render a single muted
             line instead of a bare heading. Suppressed in edit mode (the
             editor always shows the Add-Reference affordance) and whenever any
             block rendered. */}
          {!isEditing && !appendixHasContent && (
            <p
              className="text-xs italic"
              style={{ color: roleColor(theme, 'muted') }}
              data-testid="appendix-empty-fallback"
            >
              References and data sources for this report are being compiled.
            </p>
          )}
        </EditorialSectionShell>
      )}

      {immutableArtifactMode && isVisible('blindSpots') && (
        <div style={sectionStyle('blindSpots')}>
          <BlindSpotsAppendix
            report={report}
            theme={theme}
            isEditing={isEditing}
            headingOverrides={headingOverrides}
            setHeading={setHeading}
            suppressWhenEmpty
          />
        </div>
      )}
    </div>
  );
});

ReferencesSection.displayName = 'ReferencesSection';

export { ReferencesSection };
